// Client Roadmap Service
// This service manages client-specific roadmap data stored in Firestore

import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import roadmapTemplateService from './roadmapTemplateService';
import { statusFromTemplate, normalizeClientRoadmapStatus } from '../utils/roadmapStatus';

class ClientRoadmapService {
  /**
   * Get client roadmap templates
   * If no roadmap exists, initialize it from templates
   */
  async getClientRoadmap(clientId) {
    try {
      const accountDoc = await getDoc(doc(db, 'accounts', clientId));
      if (!accountDoc.exists()) {
        throw new Error('Client account not found');
      }

      const accountData = accountDoc.data();
      
      // If client has a roadmap, return it
      if (accountData.roadmapTemplates && Array.isArray(accountData.roadmapTemplates)) {
        return accountData.roadmapTemplates;
      }

      // Otherwise, initialize from templates
      const templates = await roadmapTemplateService.getAllTemplates();
      const clientRoadmap = templates.map((template, index) => ({
        ...template,
        clientRoadmapId: Date.now() + index, // Unique ID for this client's roadmap
        templateId: template.id, // Keep reference to original template
        status: statusFromTemplate(template) // Seed from the template's saved status
      }));

      // Save the initialized roadmap
      await this.saveClientRoadmap(clientId, clientRoadmap);
      
      return clientRoadmap;
    } catch (error) {
      console.error('Error getting client roadmap:', error);
      throw error;
    }
  }

  /**
   * Save client roadmap templates to Firestore
   */
  async saveClientRoadmap(clientId, roadmapTemplates) {
    try {
      const accountRef = doc(db, 'accounts', clientId);
      await updateDoc(accountRef, {
        roadmapTemplates: roadmapTemplates,
        roadmapUpdatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving client roadmap:', error);
      throw error;
    }
  }

  /**
   * Add a new template to client roadmap
   */
  async addTemplateToClientRoadmap(clientId, template) {
    try {
      const roadmap = await this.getClientRoadmap(clientId);
      const newTemplate = {
        ...template,
        clientRoadmapId: Date.now() + Math.random(),
        templateId: template.id || null,
        status: normalizeClientRoadmapStatus(template.status) // Default status
      };
      roadmap.push(newTemplate);
      await this.saveClientRoadmap(clientId, roadmap);
      return newTemplate;
    } catch (error) {
      console.error('Error adding template to client roadmap:', error);
      throw error;
    }
  }

  /**
   * Update a template in client roadmap
   */
  async updateTemplateInClientRoadmap(clientId, templateId, updates) {
    try {
      const roadmap = await this.getClientRoadmap(clientId);
      const templateIdNum = parseFloat(templateId);
      const templateIdStr = String(templateId);
      
      const index = roadmap.findIndex(t => 
        t.clientRoadmapId === templateIdNum || 
        t.clientRoadmapId === templateIdStr ||
        t.clientRoadmapId === templateId ||
        t.id === templateIdNum ||
        t.id === templateIdStr ||
        t.id === templateId
      );
      
      if (index !== -1) {
        roadmap[index] = { ...roadmap[index], ...updates };
        await this.saveClientRoadmap(clientId, roadmap);
        return roadmap[index];
      }
      return null;
    } catch (error) {
      console.error('Error updating template in client roadmap:', error);
      throw error;
    }
  }

  /**
   * Delete a template from client roadmap
   */
  async deleteTemplateFromClientRoadmap(clientId, templateId) {
    try {
      const roadmap = await this.getClientRoadmap(clientId);
      const templateIdNum = parseFloat(templateId);
      const templateIdStr = String(templateId);
      
      const filtered = roadmap.filter(t => 
        t.clientRoadmapId !== templateIdNum &&
        t.clientRoadmapId !== templateIdStr &&
        t.clientRoadmapId !== templateId &&
        t.id !== templateIdNum &&
        t.id !== templateIdStr &&
        t.id !== templateId
      );
      await this.saveClientRoadmap(clientId, filtered);
      return true;
    } catch (error) {
      console.error('Error deleting template from client roadmap:', error);
      throw error;
    }
  }

  /**
   * Reorder templates in client roadmap
   */
  async reorderClientRoadmap(clientId, reorderedTemplates) {
    try {
      // Update order values
      const updatedTemplates = reorderedTemplates.map((template, index) => ({
        ...template,
        order: index + 1
      }));
      
      await this.saveClientRoadmap(clientId, updatedTemplates);
      return updatedTemplates;
    } catch (error) {
      console.error('Error reordering client roadmap:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const clientRoadmapService = new ClientRoadmapService();

export default clientRoadmapService;

